import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, CardHeader, CardContent, Box, Typography, Chip, Button,
  TextField, MenuItem, Alert, CircularProgress, Divider,
} from '@mui/material';
import { AdminService } from '../../services/adminService';

/**
 * Crédits d'usage (P5 / CONCEPT_MODELE_QUOTAS §6) — soldes + octroi manuel.
 *
 * Le canal de regain est le pendant obligatoire du plancher à vie : à 2 scans,
 * un parent qui les grille sur deux photos floues n'a aucun recours (§6.3).
 * C'est un outil de support, pas une fonctionnalité de croissance.
 */

const LIMIT_TYPES = [
  { value: 'lesson_scan', label: 'Scan de leçon' },
  { value: 'sage_session', label: 'Session Vieux Sage' },
];

/**
 * Motifs proposés — la liste n'a AUCUNE valeur par défaut : sans motif inscrit,
 * on ne pourra jamais mesurer quel geste convertit (§6.2, D-7).
 */
const REASONS = [
  { value: 'support_scan_illisible', label: 'Support — scan illisible / photo floue' },
  { value: 'support_incident', label: 'Support — incident technique' },
  { value: 'geste_commercial', label: 'Geste commercial' },
  { value: 'retention', label: 'Rétention' },
  { value: 'recuperation', label: 'Récupération (utilisateur parti)' },
  { value: 'felicitations', label: 'Félicitations (palier, série, fin de biome)' },
  { value: 'recommandation', label: 'Recommandation à un proche' },
];

function BalanceLine({ label, status }) {
  if (!status) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" sx={{ minWidth: 170 }}>{label}</Typography>
        <Chip size="small" label="—" />
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Typography variant="body2" sx={{ minWidth: 170 }}>{label}</Typography>
      {status.unlimited ? (
        <Chip size="small" color="success" label="Illimité (plan payant)" />
      ) : (
        <Chip
          size="small"
          color={status.remaining > 0 ? 'primary' : 'error'}
          label={`${status.remaining} restant${status.remaining === 1 ? '' : 's'}`}
        />
      )}
    </Box>
  );
}

export default function UsageCreditsCard({ userId }) {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [limitType, setLimitType] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [granting, setGranting] = useState(false);

  const fetchCredits = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await AdminService.getUserUsageCredits(userId);
      setCredits(res?.credits ?? null);
    } catch (err) {
      console.error('[UsageCredits] chargement:', err);
      setError(err?.response?.data?.error || 'Impossible de charger les soldes');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchCredits(); }, [fetchCredits]);

  const canGrant = !!limitType && !!reason && !granting;

  const handleGrant = async () => {
    if (!canGrant) return;
    setGranting(true);
    setError(null);
    setSuccess(null);
    try {
      // 1 crédit par geste (D-11). L'octroi n'est jamais présenté comme un dû.
      await AdminService.grantUserUsageCredit(userId, {
        limitType,
        reason,
        note: note.trim() || undefined,
        amount: 1,
      });
      const label = LIMIT_TYPES.find((t) => t.value === limitType)?.label ?? limitType;
      setSuccess(`+1 crédit « ${label} » accordé.`);
      setReason('');
      setNote('');
      await fetchCredits();
    } catch (err) {
      console.error('[UsageCredits] octroi:', err);
      setError(err?.response?.data?.error || "L'octroi a échoué");
    } finally {
      setGranting(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Crédits d'usage"
        subheader="Plancher à vie du plan gratuit — scan de leçon et Vieux Sage"
      />
      <CardContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <BalanceLine label="Scan de leçon" status={credits?.lessonScan} />
            <BalanceLine label="Session Vieux Sage" status={credits?.sageSession} />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Accorder un crédit
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Un crédit par geste. Le motif est obligatoire : c'est lui qui permettra
              de savoir quel cadeau convertit.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                select
                size="small"
                label="Limite"
                value={limitType}
                onChange={(e) => setLimitType(e.target.value)}
                fullWidth
              >
                {LIMIT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Motif (obligatoire)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
              >
                {REASONS.map((r) => (
                  <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                ))}
              </TextField>

              <TextField
                size="small"
                label="Note (facultatif)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Ticket support, contexte…"
                fullWidth
              />

              <Box>
                <Button
                  variant="contained"
                  onClick={handleGrant}
                  disabled={!canGrant}
                >
                  {granting ? 'Octroi…' : '+1 crédit'}
                </Button>
              </Box>
            </Box>

            {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </>
        )}
      </CardContent>
    </Card>
  );
}
